import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { Position } from '@/types/workflowDefinition';
import { composeWorkflowKey, type WorkflowIdentity } from './workflowViewPrefsStore';

/**
 * Client-side persistence of node pixel positions for the workflow canvas.
 *
 * Positions are intentionally NOT stored in the workflow JSON definition:
 *   - Layout is a viewing concern, not a workflow contract. Two collaborators
 *     can drag nodes independently without producing a draft diff.
 *   - The workflow JSON stays canonical — comparing two workflow definitions
 *     no longer flips on pixel deltas.
 *   - Auto-layout on first load is cheap; we only need to persist when the
 *     user manually drags a node or applies layout.
 *
 * Each workflow entry has two branches — `draft` and `published` — so the
 * canvas can show different layouts depending on which version the user is
 * viewing. Drag/insert/delete edits always target the draft branch (the
 * editor is read-only in published view). On publish, callers invoke
 * `snapshotDraftToPublished` so the published view reflects the layout at
 * the moment of release.
 *
 * Persistence has two tiers:
 *   1. `localStorage` (this store) — instant first paint after a refresh
 *   2. Server-side `nodesMetadata.json` sidecar next to `workflow.json` —
 *      cross-device durability. Fetched on workflow load to seed this
 *      store and PUT on workflow save. See `services/nodesMetadataApi.ts`.
 *
 * Node-ID convention matches what `useWorkflowGraph` emits as React Flow
 * node IDs, so the same map is used for reads, writes, and the layout
 * engine's output:
 *   - `trigger-{name}` for triggers
 *   - `action-{scopedPath}` for actions (e.g., `action-Until/HTTP_5`)
 *
 * Agent tools are stored separately because their positions are relative
 * to the parent agent node and are keyed by `(agentName, toolName)`.
 */

const STORAGE_KEY = 'otto-workflow-layouts';
const STORAGE_VERSION = 2;
const MAX_ENTRIES = 500;

export type WorkflowMode = 'draft' | 'published';

export interface ModeBranch {
  /** React Flow node ID → absolute canvas position. */
  nodePositions: Record<string, Position>;
  /** Agent name → tool name → position relative to the parent agent. */
  toolNodePositions: Record<string, Record<string, Position>>;
}

export interface WorkflowLayout {
  draft: ModeBranch;
  published: ModeBranch;
  /** Wall-clock ms of the last write. Used for LRU eviction and sidecar sync. */
  updatedAt: number;
}

interface WorkflowLayoutsState {
  version: typeof STORAGE_VERSION;
  byWorkflow: Record<string, WorkflowLayout>;
}

interface WorkflowLayoutsActions {
  getLayout: (identity: WorkflowIdentity) => WorkflowLayout | undefined;
  getNodePositions: (identity: WorkflowIdentity, mode: WorkflowMode) => Record<string, Position>;
  getNodePosition: (identity: WorkflowIdentity, mode: WorkflowMode, nodeId: string) => Position | undefined;
  setNodePosition: (identity: WorkflowIdentity, mode: WorkflowMode, nodeId: string, position: Position) => void;
  /** Merge a partial map into the branch's nodePositions (no removals). */
  setNodePositions: (
    identity: WorkflowIdentity,
    mode: WorkflowMode,
    positions: Record<string, Position> | Map<string, Position>,
  ) => void;
  /** Replace the full set of node positions for the branch (used after auto-layout). */
  replaceNodePositions: (
    identity: WorkflowIdentity,
    mode: WorkflowMode,
    positions: Record<string, Position> | Map<string, Position>,
  ) => void;
  getToolNodePositions: (
    identity: WorkflowIdentity,
    mode: WorkflowMode,
    agentName: string,
  ) => Record<string, Position>;
  setToolNodePosition: (
    identity: WorkflowIdentity,
    mode: WorkflowMode,
    agentName: string,
    toolName: string,
    position: Position,
  ) => void;
  /**
   * Merge layout data into the branch. Only touches keys actually provided;
   * later writes overwrite per-key. Used to (a) lift legacy positions out of
   * a workflow JSON definition and (b) hydrate the store from a sidecar file.
   */
  importLayout: (
    identity: WorkflowIdentity,
    mode: WorkflowMode,
    layout: {
      nodePositions?: Record<string, Position>;
      toolNodePositions?: Record<string, Record<string, Position>>;
    },
  ) => void;
  /** Drop a specific node's position (e.g., after delete). */
  removeNode: (identity: WorkflowIdentity, mode: WorkflowMode, nodeId: string) => void;
  /** Drop an agent's tool positions when the agent is removed. */
  removeAgent: (identity: WorkflowIdentity, mode: WorkflowMode, agentName: string) => void;
  /** Drop a single tool's position when removed from an agent. */
  removeTool: (
    identity: WorkflowIdentity,
    mode: WorkflowMode,
    agentName: string,
    toolName: string,
  ) => void;
  /** Copy the draft branch over the published branch. Call after a successful publish. */
  snapshotDraftToPublished: (identity: WorkflowIdentity) => void;
  /** Drop both branches for a workflow. */
  clearLayout: (identity: WorkflowIdentity) => void;
  clearAll: () => void;
}

type WorkflowLayoutsStore = WorkflowLayoutsState & WorkflowLayoutsActions;

function isPosition(value: unknown): value is Position {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.x === 'number' && typeof v.y === 'number' && Number.isFinite(v.x) && Number.isFinite(v.y);
}

function sanitizeNodePositions(raw: unknown): Record<string, Position> {
  if (typeof raw !== 'object' || raw === null) return {};
  const out: Record<string, Position> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof k === 'string' && k.length > 0 && isPosition(v)) {
      out[k] = { x: v.x, y: v.y };
    }
  }
  return out;
}

function sanitizeToolNodePositions(raw: unknown): Record<string, Record<string, Position>> {
  if (typeof raw !== 'object' || raw === null) return {};
  const out: Record<string, Record<string, Position>> = {};
  for (const [agentName, tools] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof agentName !== 'string' || agentName.length === 0) continue;
    const innerSanitized = sanitizeNodePositions(tools);
    if (Object.keys(innerSanitized).length > 0) {
      out[agentName] = innerSanitized;
    }
  }
  return out;
}

function sanitizeBranch(raw: unknown): ModeBranch {
  if (typeof raw !== 'object' || raw === null) return { nodePositions: {}, toolNodePositions: {} };
  const obj = raw as Record<string, unknown>;
  return {
    nodePositions: sanitizeNodePositions(obj.nodePositions),
    toolNodePositions: sanitizeToolNodePositions(obj.toolNodePositions),
  };
}

function isWorkflowLayout(value: unknown): value is WorkflowLayout {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.updatedAt === 'number';
}

/**
 * Migrate a v1 entry (flat `nodePositions` / `toolNodePositions`) into the v2
 * shape (draft + published branches). The legacy data is treated as the draft
 * branch; published starts empty so the user sees the freshest layout.
 */
function migrateV1Entry(raw: Record<string, unknown>): WorkflowLayout {
  return {
    draft: {
      nodePositions: sanitizeNodePositions(raw.nodePositions),
      toolNodePositions: sanitizeToolNodePositions(raw.toolNodePositions),
    },
    published: { nodePositions: {}, toolNodePositions: {} },
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : 0,
  };
}

function sanitizeState(raw: unknown): WorkflowLayoutsState {
  const safe: WorkflowLayoutsState = { version: STORAGE_VERSION, byWorkflow: {} };
  if (typeof raw !== 'object' || raw === null) return safe;
  const obj = raw as Record<string, unknown>;
  const incomingVersion = obj.version;

  if (typeof obj.byWorkflow === 'object' && obj.byWorkflow !== null) {
    const entries = Object.entries(obj.byWorkflow as Record<string, unknown>)
      .filter(([k]) => typeof k === 'string' && k.length > 0)
      .map(([k, v]) => {
        if (!isWorkflowLayout(v)) return null;
        const layout = v as unknown as Record<string, unknown>;
        let sanitized: WorkflowLayout;
        if (incomingVersion === STORAGE_VERSION) {
          sanitized = {
            draft: sanitizeBranch(layout.draft),
            published: sanitizeBranch(layout.published),
            updatedAt: typeof layout.updatedAt === 'number' ? layout.updatedAt : 0,
          };
        } else if (incomingVersion === 1) {
          sanitized = migrateV1Entry(layout);
        } else {
          return null;
        }
        return [k, sanitized] as const;
      })
      .filter((entry): entry is readonly [string, WorkflowLayout] => entry !== null);

    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b[1].updatedAt - a[1].updatedAt);
      entries.length = MAX_ENTRIES;
    }

    for (const [k, v] of entries) safe.byWorkflow[k] = v;
  }

  return safe;
}

function evictIfOverCap(byWorkflow: Record<string, WorkflowLayout>): Record<string, WorkflowLayout> {
  const keys = Object.keys(byWorkflow);
  if (keys.length <= MAX_ENTRIES) return byWorkflow;
  const sorted = keys
    .map((k) => [k, byWorkflow[k]!.updatedAt] as const)
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_ENTRIES);
  const kept: Record<string, WorkflowLayout> = {};
  for (const [k] of sorted) kept[k] = byWorkflow[k]!;
  return kept;
}

const EMPTY_NODE_POSITIONS: Record<string, Position> = Object.freeze({}) as Record<string, Position>;
const EMPTY_BRANCH: ModeBranch = Object.freeze({
  nodePositions: EMPTY_NODE_POSITIONS,
  toolNodePositions: Object.freeze({}) as Record<string, Record<string, Position>>,
});

function toPlainPositions(
  positions: Record<string, Position> | Map<string, Position>,
): Record<string, Position> {
  if (positions instanceof Map) {
    const out: Record<string, Position> = {};
    for (const [k, v] of positions) {
      if (typeof k === 'string' && k.length > 0 && isPosition(v)) out[k] = { x: v.x, y: v.y };
    }
    return out;
  }
  const out: Record<string, Position> = {};
  for (const [k, v] of Object.entries(positions)) {
    if (typeof k === 'string' && k.length > 0 && isPosition(v)) out[k] = { x: v.x, y: v.y };
  }
  return out;
}

function emptyBranch(): ModeBranch {
  return { nodePositions: {}, toolNodePositions: {} };
}

function emptyLayout(): WorkflowLayout {
  return { draft: emptyBranch(), published: emptyBranch(), updatedAt: Date.now() };
}

function updateBranch(
  layout: WorkflowLayout,
  mode: WorkflowMode,
  next: ModeBranch,
): WorkflowLayout {
  return { ...layout, [mode]: next, updatedAt: Date.now() };
}

export const useWorkflowLayoutStore = create<WorkflowLayoutsStore>()(
  persist(
    (set, get) => ({
      version: STORAGE_VERSION,
      byWorkflow: {},

      getLayout: (identity) => get().byWorkflow[composeWorkflowKey(identity)],

      getNodePositions: (identity, mode) =>
        get().byWorkflow[composeWorkflowKey(identity)]?.[mode]?.nodePositions ?? EMPTY_NODE_POSITIONS,

      getNodePosition: (identity, mode, nodeId) =>
        get().byWorkflow[composeWorkflowKey(identity)]?.[mode]?.nodePositions[nodeId],

      setNodePosition: (identity, mode, nodeId, position) => {
        if (typeof nodeId !== 'string' || nodeId.length === 0 || !isPosition(position)) return;
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key] ?? emptyLayout();
          const branch = existing[mode];
          const nextBranch: ModeBranch = {
            ...branch,
            nodePositions: { ...branch.nodePositions, [nodeId]: { x: position.x, y: position.y } },
          };
          return {
            byWorkflow: evictIfOverCap({
              ...state.byWorkflow,
              [key]: updateBranch(existing, mode, nextBranch),
            }),
          };
        });
      },

      setNodePositions: (identity, mode, positions) => {
        const sanitized = toPlainPositions(positions);
        if (Object.keys(sanitized).length === 0) return;
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key] ?? emptyLayout();
          const branch = existing[mode];
          const nextBranch: ModeBranch = {
            ...branch,
            nodePositions: { ...branch.nodePositions, ...sanitized },
          };
          return {
            byWorkflow: evictIfOverCap({
              ...state.byWorkflow,
              [key]: updateBranch(existing, mode, nextBranch),
            }),
          };
        });
      },

      replaceNodePositions: (identity, mode, positions) => {
        const sanitized = toPlainPositions(positions);
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key] ?? emptyLayout();
          const branch = existing[mode];
          const nextBranch: ModeBranch = { ...branch, nodePositions: sanitized };
          return {
            byWorkflow: evictIfOverCap({
              ...state.byWorkflow,
              [key]: updateBranch(existing, mode, nextBranch),
            }),
          };
        });
      },

      getToolNodePositions: (identity, mode, agentName) =>
        get().byWorkflow[composeWorkflowKey(identity)]?.[mode]?.toolNodePositions[agentName] ?? EMPTY_NODE_POSITIONS,

      setToolNodePosition: (identity, mode, agentName, toolName, position) => {
        if (
          typeof agentName !== 'string' || agentName.length === 0 ||
          typeof toolName !== 'string' || toolName.length === 0 ||
          !isPosition(position)
        ) return;
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key] ?? emptyLayout();
          const branch = existing[mode];
          const nextAgentTools = {
            ...(branch.toolNodePositions[agentName] ?? {}),
            [toolName]: { x: position.x, y: position.y },
          };
          const nextBranch: ModeBranch = {
            ...branch,
            toolNodePositions: { ...branch.toolNodePositions, [agentName]: nextAgentTools },
          };
          return {
            byWorkflow: evictIfOverCap({
              ...state.byWorkflow,
              [key]: updateBranch(existing, mode, nextBranch),
            }),
          };
        });
      },

      importLayout: (identity, mode, layout) => {
        const nodePositions = layout.nodePositions ? sanitizeNodePositions(layout.nodePositions) : {};
        const toolNodePositions = layout.toolNodePositions
          ? sanitizeToolNodePositions(layout.toolNodePositions)
          : {};
        if (Object.keys(nodePositions).length === 0 && Object.keys(toolNodePositions).length === 0) return;
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key] ?? emptyLayout();
          const branch = existing[mode];
          // Merge agent tools per-agent (preserve other agents' tools).
          const mergedToolPositions: Record<string, Record<string, Position>> = { ...branch.toolNodePositions };
          for (const [agent, tools] of Object.entries(toolNodePositions)) {
            mergedToolPositions[agent] = { ...(mergedToolPositions[agent] ?? {}), ...tools };
          }
          const nextBranch: ModeBranch = {
            nodePositions: { ...branch.nodePositions, ...nodePositions },
            toolNodePositions: mergedToolPositions,
          };
          return {
            byWorkflow: evictIfOverCap({
              ...state.byWorkflow,
              [key]: updateBranch(existing, mode, nextBranch),
            }),
          };
        });
      },

      removeNode: (identity, mode, nodeId) => {
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key];
          if (!existing || !(nodeId in existing[mode].nodePositions)) return state;
          const { [nodeId]: _removed, ...rest } = existing[mode].nodePositions;
          void _removed;
          const nextBranch: ModeBranch = { ...existing[mode], nodePositions: rest };
          return {
            byWorkflow: {
              ...state.byWorkflow,
              [key]: updateBranch(existing, mode, nextBranch),
            },
          };
        });
      },

      removeAgent: (identity, mode, agentName) => {
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key];
          if (!existing || !(agentName in existing[mode].toolNodePositions)) return state;
          const { [agentName]: _removed, ...rest } = existing[mode].toolNodePositions;
          void _removed;
          const nextBranch: ModeBranch = { ...existing[mode], toolNodePositions: rest };
          return {
            byWorkflow: {
              ...state.byWorkflow,
              [key]: updateBranch(existing, mode, nextBranch),
            },
          };
        });
      },

      removeTool: (identity, mode, agentName, toolName) => {
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key];
          const branch = existing?.[mode];
          const agentTools = branch?.toolNodePositions[agentName];
          if (!existing || !branch || !agentTools || !(toolName in agentTools)) return state;
          const { [toolName]: _removedTool, ...remainingTools } = agentTools;
          void _removedTool;
          const nextAgentMap = Object.keys(remainingTools).length > 0
            ? { ...branch.toolNodePositions, [agentName]: remainingTools }
            : (() => {
                const { [agentName]: _removedAgent, ...restAgents } = branch.toolNodePositions;
                void _removedAgent;
                return restAgents;
              })();
          const nextBranch: ModeBranch = { ...branch, toolNodePositions: nextAgentMap };
          return {
            byWorkflow: {
              ...state.byWorkflow,
              [key]: updateBranch(existing, mode, nextBranch),
            },
          };
        });
      },

      snapshotDraftToPublished: (identity) => {
        const key = composeWorkflowKey(identity);
        set((state) => {
          const existing = state.byWorkflow[key];
          if (!existing) return state;
          const cloned: ModeBranch = {
            nodePositions: { ...existing.draft.nodePositions },
            toolNodePositions: Object.fromEntries(
              Object.entries(existing.draft.toolNodePositions).map(([agent, tools]) => [
                agent,
                { ...tools },
              ]),
            ),
          };
          return {
            byWorkflow: {
              ...state.byWorkflow,
              [key]: { ...existing, published: cloned, updatedAt: Date.now() },
            },
          };
        });
      },

      clearLayout: (identity) => {
        const key = composeWorkflowKey(identity);
        set((state) => {
          if (!(key in state.byWorkflow)) return state;
          const { [key]: _removed, ...rest } = state.byWorkflow;
          void _removed;
          return { byWorkflow: rest };
        });
      },

      clearAll: () => set({ byWorkflow: {} }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ version: state.version, byWorkflow: state.byWorkflow }),
      storage: {
        getItem: (name) => {
          try {
            const raw = localStorage.getItem(name);
            if (!raw) return null;
            const parsed: unknown = JSON.parse(raw);
            const stateLike = (parsed as { state?: unknown })?.state;
            return { state: sanitizeState(stateLike) } as { state: WorkflowLayoutsState };
          } catch {
            try { localStorage.removeItem(name); } catch { /* storage unavailable */ }
            return null;
          }
        },
        setItem: (name, value) => {
          try { localStorage.setItem(name, JSON.stringify(value)); } catch { /* quota/private mode */ }
        },
        removeItem: (name) => {
          try { localStorage.removeItem(name); } catch { /* noop */ }
        },
      },
    },
  ),
);

/**
 * Build the React Flow node ID used for a top-level or scoped action.
 * `scopedPath` is the slash-joined path of parent action names (excluding the
 * action itself); pass an empty string for top-level actions.
 */
export function actionNodeId(scopedPath: string, actionName: string): string {
  return scopedPath ? `action-${scopedPath}/${actionName}` : `action-${actionName}`;
}

/**
 * Build the React Flow node ID for a trigger.
 */
export function triggerNodeId(triggerName: string): string {
  return `trigger-${triggerName}`;
}

export const __internalsForTesting = {
  sanitizeState,
  evictIfOverCap,
  sanitizeNodePositions,
  sanitizeToolNodePositions,
  migrateV1Entry,
  MAX_ENTRIES,
  STORAGE_KEY,
  STORAGE_VERSION,
  EMPTY_BRANCH,
};
